import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ReportConfigV2 } from "@/common/report-configuration";
import { ReportStateReducerActionType } from "@/common/reportDesignerStateReducer";
import { useAvailableFilterFields } from "@/hooks/useAvailableFilterFields";
import { useReportDataV2 } from "@/hooks/useReportDataV2";
import { ReportTitle } from "./parts/ReportTitle";
import { ReportControlsV2 } from "./parts/ReportControlsV2";
import { ActiveFiltersV2 } from "./parts/ActiveFiltersV2";
import ReportChartViewV2, { DataItem } from "./parts/ReportChartViewV2";
import ReportTableViewV2 from "./parts/ReportTableViewV2";
import { ReportActionHandlers } from "./parts/ReportActionMenu";
import { Snapshots } from "./parts/Snapshots";
import { AiSummaryV2 } from "./parts/AiSummaryV2";
import { exportCsv } from "@/utils/exportCsv";
import EventBus from "@/lib/eventBus";
import {
  createReportSnapshotV2,
  getRefreshedAiSummaryV2,
  getReportSnapshotMetadata,
  getReportSnapshotV2,
  patchReport,
} from "@/lib/serverComm";
import { MeasureName, MEASURES } from "@/common/measures";
import { useCurrentWorkspace } from "@/store/authContext/authContext";

import { DisclaimersV2 } from "@/components/report/parts/DisclaimersV2";
import { Teaser } from "../charts/components/teaser";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import mixpanel from "@/lib/mixpanel";
import { QuickStarts } from "./QuickStarts";
import ShoePalaceCustomPanel from "./ShoePalaceCustomPanel";

interface ReportProps {
  id?: string;
  hasChanges?: boolean;
  reportConfig: ReportConfigV2;
  reportDesignerDispatch?: React.Dispatch<ReportStateReducerActionType>;
  onEdit?: () => void;
  newFilterField: string | null;
  onSnapshotViewChange?: (isSnapshotView: boolean) => void;
  showToggle?: boolean;
  size?: string; // lg, md, sm
  allowSave?: boolean;
  onRemoveFromPage?: () => void;
  isSystemReport?: boolean;
  onError?: () => void;
  disableSummaryRegeneration?: boolean; // New prop to disable summary regeneration
  slideMode?: boolean; // controls display of certain elements when displayed in deck builder
  disableNavigationOnSave?: boolean; // Disable automatic navigation after save
}

export const ReportV2: React.FC<ReportProps> = ({
  id,
  hasChanges = false,
  reportConfig,
  reportDesignerDispatch: dispatch,
  onEdit,
  newFilterField,
  onSnapshotViewChange,
  showToggle = true,
  size = "lg",
  allowSave = true,
  onRemoveFromPage,
  isSystemReport = false,
  onError,
  disableSummaryRegeneration = false, // New prop to disable summary regeneration, used for workspaces
  slideMode = false,
  disableNavigationOnSave = false,
}) => {
  const [viewMode, setViewMode] = useState<"chart" | "table">("chart");
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const currentWorkspace = useCurrentWorkspace();
  const fieldNameOverrides =
    currentWorkspace.dataset_field_configs[reportConfig.data.datasetName]
      ?.fieldNameOverrides;
  const availableFields = useAvailableFilterFields(
    reportConfig.data.datasetName,
    fieldNameOverrides,
  );
  const {
    data: liveData,
    extras: reportExtras,
    loading: isLoadingData,
    error,
  } = useReportDataV2(reportConfig);
  const [snapshotData, setSnapshotData] = useState<any[] | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any

  const exportPngRef = useRef<() => Promise<void>>();

  const prevConfigRef = useRef(reportConfig);

  // Calculate whether the show count toggle should be enabled
  // based on the active measures' alwaysShowCount property
  const allowShowCountChange = !reportConfig.data.measures.some(
    (measureName: MeasureName) => MEASURES[measureName].alwaysShowCount,
  );

  const [snapshots, setSnapshots] = useState<
    Array<{
      id: string;
      title?: string;
      created_at: string;
    }>
  >([]);

  const [snapshotView, setSnapshotView] = useState(false);
  const [activeSnapshotId, setActiveSnapshotId] = useState<string | null>(null);
  const [isHeadlineLoading, setIsHeadlineLoading] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);

  // Call onError callback when error state changes (must be in useEffect, not during render)
  useEffect(() => {
    if (error && onError) {
      onError();
    }
  }, [error, onError]);

  useEffect(() => {
    EventBus.$on("report:configFetched", (config: ReportConfigV2) => {
      // set prevConfigRef to the fetched config so that the revert button works correctly
      prevConfigRef.current = config;
    });
    return () => {
      EventBus.$off("report:configFetched");
    };
  }, []);

  const loadSnapshots = useCallback(async () => {
    if (!id) return;
    try {
      const snapshotData = await getReportSnapshotMetadata(id);
      setSnapshots(snapshotData);
    } catch (error) {
      console.error("Error loading snapshots:", error);
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      // Clear snapshots for new reports
      setSnapshots([]);
      setSnapshotView(false);
      setActiveSnapshotId(null);
      setSnapshotData(null);
      return;
    }

    loadSnapshots();
  }, [loadSnapshots, id]);

  useEffect(() => {
    if (!id) {
      // New report; nothing to do
      return;
    }

    // if aiHeadline and aiSummary are blank, we refresh them, but
    // only for editable reports
    if (
      reportConfig &&
      !reportConfig.metadata?.ai_headline &&
      !reportConfig.metadata?.ai_summary &&
      !isSystemReport &&
      dispatch &&
      !disableSummaryRegeneration
    ) {
      if (liveData.length === 0 || (isSummaryLoading && isHeadlineLoading)) {
        // If liveData is empty or both AI fields are already loading, skip refresh
        return;
      }

      setIsHeadlineLoading(true);
      setIsSummaryLoading(true);
      getRefreshedAiSummaryV2(reportConfig, liveData).then((result) => {
        // The results take a long time to come back, during which time
        // the user may have switched to a different report. I've had
        // no luck storing the id at time of effect call in a ref, so
        // we are brute forcing this with a URL check.
        const currentId = window.location.pathname.split("/").pop();
        if (currentId !== id) {
          // If the current report id doesn't match, skip updating
          setIsHeadlineLoading(false);
          setIsSummaryLoading(false);
          return;
        }
        dispatch({
          type: "patchMetadata",
          payload: {
            ai_headline: result.headline,
            ai_summary: result.summary,
            ai_headline_dirty: false, // Reset dirty state after refresh
            ai_summary_dirty: false, // Reset dirty state after refresh
            ai_headline_expanded: true, // Reset expanded state after refresh
            ai_summary_expanded: true, // Reset expanded state after refresh
          },
        });
        setIsHeadlineLoading(false);
        setIsSummaryLoading(false);
      });
    }
  }, [
    reportConfig,
    id,
    liveData,
    dispatch,
    isSummaryLoading,
    isHeadlineLoading,
    isSystemReport,
    disableSummaryRegeneration,
  ]);

  useEffect(() => {
    if (onSnapshotViewChange) {
      onSnapshotViewChange(snapshotView);
    }
  }, [snapshotView, onSnapshotViewChange]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch?.({
      type: "setTitle",
      payload: { title: e.target.value, manuallySet: true },
    });
  };

  const handleAiHeadlineChange = (headline: string) => {
    dispatch?.({
      type: "patchMetadata",
      payload: { ai_headline: headline, ai_headline_dirty: false }, // When humans edit, we assume they want to keep the AI headline
    });
  };

  const handleAiSummaryChange = (summary: string) => {
    dispatch?.({
      type: "patchMetadata",
      payload: { ai_summary: summary, ai_summary_dirty: false }, // When humans edit, we assume they want to keep the AI summary
    });
  };

  const handleSave = useCallback(() => {
    prevConfigRef.current = reportConfig;
    dispatch?.({
      type: "reportHasBeenSaved",
    });
  }, [dispatch, reportConfig]);

  const loadSnapshot = async (snapshotId: string) => {
    try {
      if (dispatch && id) {
        const snapshot = await getReportSnapshotV2(id, snapshotId);
        setSnapshotView(true);
        setActiveSnapshotId(snapshotId);
        setSnapshotData(snapshot.data);
        dispatch({ type: "setReportConfig", payload: snapshot.config });
      }
    } catch (error) {
      console.error("Error loading snapshot:", error);
      setSnapshotData(null);
    }
  };

  const returnToCurrent = () => {
    setSnapshotView(false);
    setActiveSnapshotId(null);
    setSnapshotData(null);
    if (dispatch && prevConfigRef.current) {
      dispatch({ type: "setReportConfig", payload: prevConfigRef.current });
    }
  };

  // Use snapshot data when in snapshot view, otherwise use live data
  const data = (snapshotView ? snapshotData : liveData) || [];

  // Calculate if there are small groups before filtering
  const hasHiddenSmallGroups =
    reportConfig.chart.hideSmallGroups &&
    data.some(
      (item) =>
        item.value_num_employees > 0 &&
        item.value_num_employees <= 5 &&
        !item.projected,
    );

  if ("dataUnavailable" in reportExtras && reportExtras.dataUnavailable) {
    return <Teaser fill="#999" />;
  }

  if (!reportConfig.data.measures.length && !isLoadingData) {
    const SHOE_PALACE_DOMAIN = "parity-org-00039.domo.com";
    if (currentWorkspace.domo_instance_domain === SHOE_PALACE_DOMAIN) {
      return (
        <ShoePalaceCustomPanel
          workspace={currentWorkspace}
          dispatch={dispatch ?? (() => undefined)}
        />
      );
    }

    return (
      <QuickStarts
        workspace={currentWorkspace}
        onSelectPreset={(presetKey, presetLabel) => {
          dispatch?.({ type: "setPreset", payload: presetKey });
          mixpanel.track("Select Report Preset", {
            eventDescription: presetLabel,
          });
        }}
      />
    );
  }

  const reportActions: ReportActionHandlers = {
    onRevert: () => {
      if (dispatch && prevConfigRef.current) {
        dispatch({ type: "setReportConfig", payload: prevConfigRef.current });
      }
    },
    onSaveSnapshot: async () => {
      if (liveData && reportConfig && id) {
        // Use liveData here since we're saving current state
        try {
          await createReportSnapshotV2(id, reportConfig, liveData);
          loadSnapshots(); // Refresh snapshots after saving
        } catch (error) {
          console.error("Error saving snapshot:", error);
        }
      }
    },
    onExportCsv: () => {
      if (data && data.length > 0) {
        exportCsv(data, `${reportConfig.title || "report"}.csv`);
      }
    },
    onExportPng: () => {
      if (viewMode === "chart" && exportPngRef.current) {
        exportPngRef.current();
      }
    },
    onRemoveFromPage: onRemoveFromPage,
    isSystemReport,
  };

  if (error) {
    return (
      <Card className="w-full">
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">
            No data available for this type of report
          </p>
        </CardContent>
      </Card>
    );
  }

  const handleSummaryRefresh = async () => {
    setIsSummaryLoading(true);
    const result = await getRefreshedAiSummaryV2(reportConfig, data);
    dispatch?.({
      type: "patchMetadata",
      payload: {
        ai_summary: result.summary,
        ai_summary_dirty: false, // Reset dirty state after refresh
      },
    });
    setIsSummaryLoading(false);
  };

  const handleHeadlineRefresh = async () => {
    setIsHeadlineLoading(true);
    const result = await getRefreshedAiSummaryV2(reportConfig, data);
    dispatch?.({
      type: "patchMetadata",
      payload: {
        ai_headline: result.headline,
        ai_headline_dirty: false, // Reset dirty state after refresh
      },
    });

    setIsHeadlineLoading(false);
  };

  const handleSetHeadlineExpanded = (expanded: boolean) => {
    if (!isSystemReport && !!id) {
      // we want to immediately save the expanded state
      // without updating other config values server-side.
      const reportPartial = {
        metadata: {
          ai_headline_expanded: expanded,
        },
      };
      dispatch?.({
        type: "patchMetadata",
        payload: { ai_headline_expanded: expanded },
      });
      patchReport(id, reportPartial);
    }
  };

  const handleSetSummaryExpanded = (expanded: boolean) => {
    if (!isSystemReport && !!id) {
      const reportPartial = {
        metadata: {
          ai_summary_expanded: expanded,
        },
      };
      dispatch?.({
        type: "patchMetadata",
        payload: { ai_summary_expanded: expanded },
      });
      patchReport(id, reportPartial);
    }
  };

  return (
    <div className="flex flex-col overflow-hidden">
      <Card className="flex-1 min-h-0 overflow-hidden relative">
        <CardContent className="p-3 flex flex-col overflow-hidden">
          {!slideMode && (
            <div className="flex justify-between items-center px-4 my-4">
              <ReportTitle
                title={reportConfig.title}
                isTitleFocused={isTitleFocused}
                onTitleChange={handleTitleChange}
                setIsTitleFocused={setIsTitleFocused}
                canEdit={!!dispatch && !snapshotView}
              />
              {!isTitleFocused &&
                reportExtras.methodologyNotes &&
                Object.keys(reportExtras.methodologyNotes).length > 0 && (
                  <div className="flex-grow ml-3">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="p-2.5 -m-2.5 inline-flex items-center justify-center cursor-pointer">
                            <Info className="text-primary h-5 w-5 flex-shrink-0" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px] p-4">
                          {Object.keys(reportExtras.methodologyNotes).map(
                            (key) => (
                              <div key={key}>
                                <h4 className="font-semibold mb-2">
                                  {MEASURES[key as MeasureName].label}
                                </h4>
                                {reportExtras.methodologyNotes &&
                                  reportExtras.methodologyNotes[
                                    key as MeasureName
                                  ].map((note) => (
                                    <p className="text-xs" key={note}>
                                      {note}
                                    </p>
                                  ))}
                              </div>
                            ),
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              <ReportControlsV2
                id={id}
                config={reportConfig}
                canSave={hasChanges && !snapshotView && allowSave}
                onSave={handleSave}
                onEdit={onEdit}
                viewMode={viewMode}
                showToggle={showToggle}
                onViewModeChange={setViewMode}
                allowShowCountChange={allowShowCountChange}
                onShowCountChange={(val: boolean) =>
                  dispatch?.({
                    type: "setChartSetting",
                    payload: { field: "showCount", value: val },
                  })
                }
                actions={reportActions}
                disableNavigationOnSave={disableNavigationOnSave}
              />
            </div>
          )}

          {reportConfig.data.filters.length > 0 &&
            !slideMode &&
            availableFields && (
              <ActiveFiltersV2
                filters={reportConfig.data.filters}
                datasetName={reportConfig.data.datasetName}
                newFilterField={newFilterField}
                reportDesignerDispatch={dispatch}
                disabled={snapshotView}
              />
            )}

          {error ? (
            <div className="text-center">
              <p className="text-muted-foreground">
                No data available for this type of report
              </p>
            </div>
          ) : isLoadingData ? (
            <div className="flex-1 min-h-0 p-4">
              <div className="w-full h-full min-h-[500px] bg-gray-100 dark:bg-gray-700 rounded animate-pulse">
                <div className="text-center">
                  <p className="pt-20 text-muted-foreground">Loading data...</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0">
              {(() => {
                // Filter out small groups if enabled
                const filteredData = reportConfig.chart.hideSmallGroups
                  ? data.filter(
                      (item) =>
                        !item.value_num_employees ||
                        item.value_num_employees > 5 ||
                        item.projected,
                    )
                  : data;

                // Transform data to use either value_num or value_pct based on showCount
                const transformedData = filteredData.map((item) => ({
                  ...item,
                  value: reportConfig.chart.showCount
                    ? item.value_num
                    : item.value_pct,
                  value_num_employees: item.value_num_employees,
                })) as DataItem[];

                return viewMode === "chart" ? (
                  <ReportChartViewV2
                    size={size}
                    data={transformedData}
                    reportExtras={reportExtras}
                    reportConfig={reportConfig}
                    reportDesignerDispatch={dispatch}
                    onExportPng={(fn: () => Promise<void>) => {
                      exportPngRef.current = fn;
                    }}
                  />
                ) : (
                  <ReportTableViewV2
                    data={transformedData}
                    reportConfig={reportConfig}
                  />
                );
              })()}
              {reportConfig.metadata.ai_headline && (
                <AiSummaryV2
                  aiHeadline={reportConfig.metadata.ai_headline}
                  aiSummary={reportConfig.metadata.ai_summary}
                  showSummary={!slideMode}
                  refreshAiField={(field: string) => {
                    if (dispatch) {
                      if (field === "headline") {
                        handleHeadlineRefresh();
                      } else if (field === "summary") {
                        handleSummaryRefresh();
                      }
                    }
                  }}
                  headlineStale={reportConfig.metadata.ai_headline_dirty}
                  summaryStale={reportConfig.metadata.ai_summary_dirty}
                  editable={!!dispatch && !snapshotView}
                  isSummaryLoading={isSummaryLoading}
                  isHeadlineLoading={isHeadlineLoading}
                  onTitleChange={handleAiHeadlineChange}
                  onSummaryChange={handleAiSummaryChange}
                  headlineExpanded={reportConfig.metadata.ai_headline_expanded}
                  summaryExpanded={reportConfig.metadata.ai_summary_expanded}
                  setHeadlineExpanded={handleSetHeadlineExpanded}
                  setSummaryExpanded={handleSetSummaryExpanded}
                />
              )}

              {!slideMode && (
                <DisclaimersV2
                  data={data}
                  reportConfig={reportConfig}
                  hasHiddenSmallGroups={hasHiddenSmallGroups}
                />
              )}
            </div>
          )}

          {!slideMode && snapshots.length > 0 && (
            <Snapshots
              snapshots={snapshots}
              returnToCurrent={returnToCurrent}
              loadSnapshot={loadSnapshot}
              activeSnapshotId={activeSnapshotId}
              snapshotView={snapshotView}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default React.memo(ReportV2);
